import store from '@/store';
import notificationTerm from '@/functions/notificationTerminal.js'
import { ConfigName } from '@/common/sharedDefinitions/commonPlotDefinitions'
import { mappingFit } from '@/common/sharedDefinitions/reportDefinitions'

const SpecialDeleteName = '____DELETE____'

const NameFilesConfig = {
    dataSort: 'dataSort',
};

const PrefixUnits = {
    12: 'T',
    9: 'G',
    6: 'M',
    3: 'k',
    0: '',
    '-3': 'm',
    '-6': 'μ',
    '-9': 'n',
    '-12': 'p',
};

const timeEquivalent = {
    s: 1,
    min: 60,
    hours: 3600,
    day: 86400,
};

const convertionFactor = function (configuration, input, output) {
    return convertionFactorBase(configuration[input], configuration[output]);
};
const convertionFactorBase = function (inputScale, outputScale) {
    const difExp = inputScale - outputScale;
    const result = Math.pow(10, difExp);
    return result;
};
const isValidValue = function(value){
    return typeof(value) !== 'undefined' && value !== null
}

//Create all the convertion and labeling in the axis. Works with the units values

const TypeAxis = {
    current: {
        id: 'current',
        getConvertFactor: function (configuration) {
            return convertionFactor(
                configuration,
                this.inputConfig,
                this.outputConfig,
            );
        },
        inputConfig: 'I_IN',
        outputConfig: 'I_OUT',
        getAxisLabel: function (configuration) {
            const outPref = PrefixUnits[configuration[this.outputConfig]];
            return `Current / ${outPref}A`;
        },
        complex: false,
    },
    potential: {
        id: 'potential',
        getConvertFactor: function (configuration) {
            return convertionFactor(
                configuration,
                this.inputConfig,
                this.outputConfig,
            );
        },
        inputConfig: 'E_IN',
        outputConfig: 'E_OUT',
        getAxisLabel: function (configuration) {
            const outPref = PrefixUnits[configuration[this.outputConfig]];
            return `Potential / ${outPref}V`;
        },
        complex: false,
    },
    charge: {
        id: 'charge',
        getConvertFactor: function (configuration) {
            return convertionFactor(
                configuration,
                this.inputConfig,
                this.outputConfig,
            );
        },
        inputConfig: 'Q_IN',
        outputConfig: 'Q_OUT',
        getAxisLabel: function (configuration) {
            const outPref = PrefixUnits[configuration[this.outputConfig]];
            return `Charge / ${outPref}C`;
        },
        complex: false,
    },
    impedance: {
        id: 'impedance',
        getConvertFactor: function (configuration) {
            return convertionFactor(
                configuration,
                this.inputConfig,
                this.outputConfig,
            );
        },
        inputConfig: 'Z_IN',
        outputConfig: 'Z_OUT',
        getAxisLabel: function (configuration) {
            const outPref = PrefixUnits[configuration[this.outputConfig]];
            return `Impedance / ${outPref}Ω`;
        },
        complex: true,
    },
    frequency: {
        id: 'frequency',
        getConvertFactor: function () {
            return 1;
        },
        inputConfig: 'Z_IN',
        outputConfig: 'Z_OUT',
        getAxisLabel: function () {
            return 'Frequency / Hz';
        },
        complex: false,
    },
    time: {
        id: 'time',
        getConvertFactor: function (configuration) {
            const inputSeconds =
                timeEquivalent[configuration[this.inputConfig]]; //Convert first to seconds
            const outUnitsFact =
                inputSeconds / timeEquivalent[configuration[this.outputConfig]];
            return outUnitsFact;
        },
        inputConfig: 'T_IN',
        outputConfig: 'T_OUT',
        getAxisLabel: function (configuration) {
            const outVal = configuration[this.outputConfig];
            return `Time / ${outVal}`;
        },
        complex: false,
    },
};

const newConfiguration = {
    mappingElem: {
        ['Flip x']: {},
        ['Flip y']: {},
				['Differentiate']: {},
				['Accumulate']: {},
				['Outlier Rejection']: {},
        ['Offset x']: {
            [ConfigName.OffsetXPreProc]: {
                name: 'Offset',
                parse: parseFloat,
            }
		},
        ['Offset y']: {
            [ConfigName.OffsetYPreProc]: {
                name: 'Offset',
                parse: parseFloat,
            }
		},
        LPF: {
            [ConfigName.FCSLPFPreProc]: {
                name: 'fc',
                parse: parseFloat,
            },
            [ConfigName.OrderLPFPreProc]: {
                name: 'order',
                parse: parseInt,
            },
        },
        HPF: {
            [ConfigName.FCSHPFPreProc]: {
                name: 'fc',
                parse: parseFloat,
            },
            [ConfigName.OrderHPFPreProc]: {
                name: 'order',
                parse: parseInt,
            },
        },
        BPF: {
            [ConfigName.F1BPFPreProc]: {
                name: 'f1',
                parse: parseFloat,
            },
            [ConfigName.F2BPFPreProc]: {
                name: 'f2',
                parse: parseFloat,
            },
            [ConfigName.OrderBPFPreProc]: {
                name: 'order',
                parse: parseInt,
            },
        },
        BSF: {
            [ConfigName.F1BSFPreProc]: {
                name: 'f1',
                parse: parseFloat,
            },
            [ConfigName.F2BSFPreProc]: {
                name: 'f2',
                parse: parseFloat,
            },
            [ConfigName.OrderBSFPreProc]: {
                name: 'order',
                parse: parseInt,
            },
        },
        ['Savitzky-Golay']: {
            [ConfigName.WindowSavitzkyGolayPreProc]: {
                name: 'window',
                parse: parseFloat,
            },
            [ConfigName.OrderSavitzkyGolayPreProc]: {
                name: 'order',
                parse: parseInt,
            },
        },
        ['Moving average']: {
            [ConfigName.WindowMedianAveragePreProc]: {
                name: 'window',
                parse: parseInt,
            },
        },
        ['Crop']: {
            RANGE_MIN_Y_FLOAT: {
                name: 'ymin',
                parse: parseFloat,
            },
            RANGE_MAX_Y_FLOAT: {
                name: 'ymax',
                parse: parseFloat,
            },
            RANGE_MIN_FLOAT: {
                name: 'xmin',
                parse: parseFloat,
            },
            RANGE_MAX_FLOAT: {
                name: 'xmax',
                parse: parseFloat,
            },
        },
        ['Median filter']: {
            [ConfigName.WindowMedianFilterPreProc]: {
                name: 'window',
                parse: parseInt,
            },
        },
    },
    mappingBaseline: {
        Offset: 'Minimum Offset',
        Linear: 'CurveFitBaseLine',
        Quadratic: 'CurveFitBaseLine',
        'Inverse square root': 'CurveFitBaseLine',
        'Semilog x': 'CurveFitBaseLine',
        'Semilog y': 'CurveFitBaseLine',
        'Double log': 'CurveFitBaseLine',
        'Exponential x': 'CurveFitBaseLine',
        'Exponential y': 'CurveFitBaseLine',
        'Michaelis-Menten': 'CurveFitBaseLine',
        'Iterative-Poly': 'IterPoly BL',
    },
    sharedVarsMapping: {
        [ConfigName.ReqExtractedValsDiscrete]: {
            name: 'showExtractedValues',
            parse: null,
        },
        EXCLUDE_CONC_1DARRAY: {
            name: 'OmitObservations',
            parse: null,
        },
    },
    getAndDelete(object, key) {
        const value = object[key];
        delete object[key];
        return value;
    },
    mapNewConfiguration(configuration) {
        // Make a copy of the configuration then we don't afect the current value
        const updatedConfiguration = JSON.parse(JSON.stringify(configuration));

        // Save the preprocessing values so these are added after the range
        const mappedPreproc = [];
        if (ConfigName.PreprocessingList in updatedConfiguration) {
            for (const preprocesing of updatedConfiguration[ConfigName.PreprocessingList]) {
                const Settings = {};
                if (typeof preprocesing === 'object') {
                    mappedPreproc.push(preprocesing);
                    continue;
                }
                let newPreprocess = {};
                if ( preprocesing === 'Baseline') {
                    //Baseline is a special case
                    const baselineType = this.getAndDelete(updatedConfiguration, ConfigName.Baseline_Type);

                    newPreprocess = {
                        Type: this.mappingBaseline[baselineType],
                        Settings: {},
                    };

                    if (newPreprocess.Type === 'IterPoly BL') {
                        newPreprocess.Settings.SSE_min = parseFloat(this.getAndDelete(updatedConfiguration, ConfigName.SSEBaseLineMultiple));
                        newPreprocess.Settings.Iterations = parseFloat(this.getAndDelete(updatedConfiguration, ConfigName.OrderBaseLineMultiple));
                    } else if (newPreprocess.Type === 'CurveFitBaseLine')
                        newPreprocess.Settings.ModelType = baselineType;
                    // Verifies if range is defined
                    if (isValidValue(updatedConfiguration[ConfigName.rangeMinBL]) && updatedConfiguration[ConfigName.rangeMinBL] !== '')
                        newPreprocess.Settings.xmin = parseFloat(this.getAndDelete(updatedConfiguration, ConfigName.rangeMinBL));
                    if (isValidValue(updatedConfiguration[ConfigName.rangeMaxBL]) && updatedConfiguration[ConfigName.rangeMaxBL] !== '')
                        newPreprocess.Settings.xmax = parseFloat(this.getAndDelete(updatedConfiguration, ConfigName.rangeMaxBL));

                } else if (preprocesing ==='Extrapolate'){
                    //Extrapolate is a special case submenu
                    newPreprocess = {Type: 'ExtrapolateCurve', Settings: {}};
                    newPreprocess.Settings.ModelType = this.getAndDelete(updatedConfiguration, ConfigName.Extrapolate_Type);
                    // Verifies if range is defined
                    if (isValidValue(updatedConfiguration[ConfigName.ExtrapolateFromPreProc]) && updatedConfiguration[ConfigName.ExtrapolateFromPreProc] !== '')
                        newPreprocess.Settings.xmin = parseFloat(
                            this.getAndDelete(updatedConfiguration, ConfigName.ExtrapolateFromPreProc));
                    if (isValidValue(updatedConfiguration[ConfigName.ExtrapolateToPreProc]) && updatedConfiguration[ConfigName.ExtrapolateToPreProc] !== '')
                        newPreprocess.Settings.xmax = parseFloat(this.getAndDelete(updatedConfiguration, ConfigName.ExtrapolateToPreProc));
				}else {
                    if(typeof(this.mappingElem[preprocesing])=== 'undefined'){
                        const index = configuration[ConfigName.PreprocessingList].indexOf(preprocesing);
                        configuration[ConfigName.PreprocessingList].splice(index, 1);
                        notificationTerm.addNotification(
                            notificationTerm.typesNotif.WARNING,
                            'Removing unknown preprocessing',
                            `Removing '${preprocesing}'. Please validate the preprocessing configured`,
                            'Plot definition',
                            15000,
                        );
                        continue;
                    }
                    for (const [key, value] of Object.entries(this.mappingElem[preprocesing])) {
                        if (typeof updatedConfiguration[key] === 'undefined')
                            throw `'${key}' for preprocessing ${preprocesing} not found, please contact the administrator of the system`;
                        const propValue = this.getAndDelete(updatedConfiguration,key);
                        Settings[value.name] = (typeof propValue === 'string' || propValue instanceof String
                                                ? value.parse(propValue)
                                                : propValue);
                    }
                    newPreprocess = {
                        Type: preprocesing,
                        Settings,
                    };
                }
                mappedPreproc.push(newPreprocess);
            }
        }

        // Writes the preprocessing with the new structure and remove old
        delete updatedConfiguration[ConfigName.PreprocessingList];
        updatedConfiguration[ConfigName.BasePreprocessing] = mappedPreproc;

        if (ConfigName.BaseCurveFit in updatedConfiguration) {
            // Add the common variables on the Fits
            for (const [key, value] of Object.entries(this.sharedVarsMapping)) {
                if (!isValidValue(updatedConfiguration[key]))
                    continue;
                const propValue = this.getAndDelete(updatedConfiguration, key);
                for (const fit of Object.values(updatedConfiguration[ConfigName.BaseCurveFit]))
                    fit[value.name] =(value.parse !== null && (typeof propValue === 'string' || propValue instanceof String)
                                      ? value.parse(propValue)
                                      : propValue);
            }
            // Mapping functionality
            if (ConfigName.BaseAxisProperties in updatedConfiguration
                && updatedConfiguration[ConfigName.BaseAxisProperties][ConfigName.MappingDiscrete]
                && ConfigName.MappingScanDiscrete in updatedConfiguration[ConfigName.BaseAxisProperties]
            ) {
                const propValue = updatedConfiguration[ConfigName.BaseAxisProperties][ConfigName.MappingScanDiscrete];

                for (const fit of Object.values(updatedConfiguration[ConfigName.BaseCurveFit])) {
                    fit.Mapping = { Scan: parseInt(propValue) };
                }
            }
        }
        return updatedConfiguration;
    },
    inverseMapNewConfiguration(parent, payload, configuration, generalConfig, sectionconfig) {
        // Mapping functionality
        // Not require to map inversely, because this is done on the Axis mapping

        if (ConfigName.BaseCurveFit in payload) {
            for (const [key, value] of Object.entries(this.sharedVarsMapping)) {
                const fits = Object.values(payload[ConfigName.BaseCurveFit]);
                const fitValueDefined = fits.find((fit) => isValidValue(fit[value.name]))
                if (!isValidValue(fitValueDefined)) {
                    // Value not defined on the fit, then continue
                    continue;
                }
                configuration[key] = fitValueDefined[value.name]
            }

            generalConfig[sectionconfig] = {}
            //Defines auxiliar variables
            for(const [fit, fitConfigs] of Object.entries(payload[ConfigName.BaseCurveFit])){
                const fitKey = Object.keys(mappingFit).find((fitSearch) => mappingFit[fitSearch].name === fit)
                if(!isValidValue(fitKey)){
                    const warnTitle = 'Fit not identified';
                    const warnText = `Fit '${fit}' not identified. Element skipped`;
                    const warnZone = 'Extract from Payload';
                    parent.$notifTerm.addNotification(parent.$notifTerm.typesNotif.WARNING, warnTitle, warnText, warnZone);
                    continue
                }
                if(!isValidValue(configuration[ConfigName.BaseCurveFit])){
                    configuration[ConfigName.BaseCurveFit] = {}
                }
                generalConfig[sectionconfig][fitKey] = true
                configuration[ConfigName.BaseCurveFit][fit] = fitConfigs
            }
        }

        //Preprocessing
        if (ConfigName.BasePreprocessing in payload) {
            configuration[ConfigName.PreprocessingList] = []
            for(const preproc of payload[ConfigName.BasePreprocessing]){
                if(preproc.Type in this.mappingElem){
                    for (const [key, value] of Object.entries(this.mappingElem[preproc.Type])) {
                        if(!isValidValue(preproc.Settings[value.name]))
                            continue;
                        configuration[key] = preproc.Settings[value.name];
                    }
                    configuration[ConfigName.PreprocessingList].push(preproc.Type)
                }else if(preproc.Type === 'ExtrapolateCurve'){
                    if(isValidValue(preproc.Settings.xmax))
                        configuration[ConfigName.ExtrapolateToPreProc] = preproc.Settings.xmax;
                    if(isValidValue(preproc.Settings.xmin))
                        configuration[ConfigName.ExtrapolateFromPreProc] = preproc.Settings.xmin;
                    configuration[ConfigName.Extrapolate_Type] = preproc.Settings.ModelType
                    configuration[ConfigName.PreprocessingList].push('Extrapolate')
                }else{
                    //Checks if it's baseline
                    const baselineType = Object.keys(this.mappingBaseline).find(key => this.mappingBaseline[key] === preproc.Type);
                    if(isValidValue(baselineType)){
                        configuration[ConfigName.PreprocessingList].push('Baseline')
                        if (preproc.Type === 'IterPoly BL') {
                            configuration[ConfigName.SSEBaseLineMultiple] = preproc.Settings.SSE_min;
                            configuration[ConfigName.OrderBaseLineMultiple] = preproc.Settings.Iterations;
                            configuration[ConfigName.Baseline_Type] = 'Iterative-Poly'
                        } else if (preproc.Type === 'CurveFitBaseLine'){
                            configuration[ConfigName.Baseline_Type] = preproc.Settings.ModelType;
                        }else{
                            configuration[ConfigName.Baseline_Type] = 'Minimum Offset'
                        }

                        if(isValidValue(preproc.Settings.xmax))
                            configuration[ConfigName.rangeMaxBL] = preproc.Settings.xmax;
                        if(isValidValue(preproc.Settings.xmin))
                            configuration[ConfigName.rangeMinBL] = preproc.Settings.xmin;
                    }else{
                        const warnTitle = 'Preprocessing not identified';
                        const warnText = `Preprocessing '${preproc.Type}' not identified. Element skipped`;
                        const warnZone = 'Extract from Payload';
                        parent.$notifTerm.addNotification(parent.$notifTerm.typesNotif.WARNING, warnTitle, warnText, warnZone);
                        continue
                    }
                }

            }
        }
    },
    getAxisProperties(configuration, generalConfig) {
        const AxisProperties = {
            LogX: configuration[ConfigName.BaseAxisProperties].LOGX_BOOL || false,
            LogY: configuration[ConfigName.BaseAxisProperties].LOGY_BOOL || false,
            MeasuredParameter: generalConfig[ConfigName.notationFit],
            Unit: generalConfig[ConfigName.fitLabel],
        };
        const xAxisType = configuration[ConfigName.BasePropStudy][ConfigName.xAxisType];
        const yAxisType = configuration[ConfigName.BasePropStudy][ConfigName.yAxisType];
        let xAxisLabel = '';
        let yAxisLabel = '';

        if (isValidValue(xAxisType) && xAxisType !== '') {
            xAxisLabel = TypeAxis[xAxisType].getAxisLabel(generalConfig);
            AxisProperties.Xscale = TypeAxis[xAxisType].getConvertFactor(generalConfig);
        } else if (isValidValue(configuration[ConfigName.GenericXAxisLabel])) {
            xAxisLabel = configuration[ConfigName.GenericXAxisLabel];
            if (isValidValue(generalConfig[ConfigName.GenericXAxisInputUnit]) && isValidValue(generalConfig[ConfigName.GenericXAxisOutputUnit])) {
                AxisProperties.Xscale = convertionFactorBase(
                    generalConfig[ConfigName.GenericXAxisInputUnit],
                    generalConfig[ConfigName.GenericXAxisOutputUnit],
                );
            }
        }
        if (isValidValue(yAxisType) && yAxisType !== '') {
            yAxisLabel = TypeAxis[yAxisType].getAxisLabel(generalConfig);
            AxisProperties.Yscale =TypeAxis[yAxisType].getConvertFactor(generalConfig);
        } else if (isValidValue(configuration[ConfigName.GenericYAxisLabel])) {
            yAxisLabel = configuration[ConfigName.GenericYAxisLabel];
            if (isValidValue(generalConfig[ConfigName.GenericYAxisInputUnit]) && isValidValue(generalConfig[ConfigName.GenericYAxisOutputUnit])) {
                AxisProperties.Yscale = convertionFactorBase(
                    generalConfig[ConfigName.GenericYAxisInputUnit],
                    generalConfig[ConfigName.GenericYAxisOutputUnit],
                );
            }
        }
        if(isValidValue(generalConfig.SWAP_UNIT) && generalConfig.SWAP_UNIT){
            AxisProperties.AlternativeUnit = {
                Unit: generalConfig.ALT_UNIT,
                Scale: generalConfig.CONV_FACT_UNIT,
                Offset: generalConfig.CONV_OFFS_UNIT,
            }
        }
        return { xAxisLabel, yAxisLabel, AxisProperties };
    },
    inverseAxisProperties(AxisProperties, GeneralUnit, configuration, generalConfig){
        if(AxisProperties.LogX){
            if(!isValidValue(configuration[ConfigName.BaseAxisProperties]))
                configuration[ConfigName.BaseAxisProperties] = {}
            configuration[ConfigName.BaseAxisProperties].LOGX_BOOL = true
        }
        if(AxisProperties.LogY){
            if(!isValidValue(configuration[ConfigName.BaseAxisProperties]))
                configuration[ConfigName.BaseAxisProperties] = {}
            configuration[ConfigName.BaseAxisProperties].LOGY_BOOL = true
        }
        generalConfig[ConfigName.notationFit] = AxisProperties.MeasuredParameter
        generalConfig[ConfigName.fitLabel] = AxisProperties.Unit

        if(isValidValue(AxisProperties.AlternativeUnit)){
            generalConfig.SWAP_UNIT = true
            generalConfig.ALT_UNIT = AxisProperties.AlternativeUnit.Unit ||''
            generalConfig.CONV_FACT_UNIT = AxisProperties.AlternativeUnit.Scale || 1.0
            generalConfig.CONV_OFFS_UNIT = AxisProperties.AlternativeUnit.Offset || 0.0
        }

        if(isValidValue(GeneralUnit)){
            for(const axiDefs of Object.values(TypeAxis)){
                if(isValidValue(GeneralUnit[axiDefs.inputConfig]) && GeneralUnit[axiDefs.inputConfig] !== '')
                    generalConfig[axiDefs.inputConfig] = GeneralUnit[axiDefs.inputConfig];
                if(isValidValue(GeneralUnit[axiDefs.outputConfig]) && GeneralUnit[axiDefs.outputConfig] !== '')
                    generalConfig[axiDefs.outputConfig] = GeneralUnit[axiDefs.outputConfig];
            }
        }
    },
    mapBackendPayloadfromConfig(configuration, generalConfig, mainName){
        const { yAxisLabel, xAxisLabel, AxisProperties } = this.getAxisProperties(configuration, generalConfig);

        const Titles = {
            XfitLabel: `${generalConfig[ConfigName.notationFit]} / ${generalConfig[ConfigName.fitLabel]}`,
            Xlabel: xAxisLabel,
            Ylabel: yAxisLabel,
            MainName: mainName,
        };
        configuration = this.mapNewConfiguration(configuration);

        return {
            Titles,
            AxisProperties,
            CurveFit: configuration.CurveFit || {},
            PreProcessing: configuration.PreProcessing || {},
            FeatureCheck: configuration.FeatureCheck || {},
        }
    },
    mapConfigFromBackendPayload(parent, backendPayload, configuration, generalConfig, sectionconfig){
        const {AxisProperties, GeneralUnit, Titles, FeatureCheck, Properties} = backendPayload
        const mainName = (Titles || {}).MainName
        if(isValidValue(FeatureCheck)){
            generalConfig[sectionconfig][ConfigName.FeatureCheckActivate] = true
            configuration.FeatureCheck = FeatureCheck
        }
        if(isValidValue(Properties)){
            if(!isValidValue(configuration[ConfigName.BasePropStudy]))
                configuration[ConfigName.BasePropStudy] = {};
            if(isValidValue(Properties[ConfigName.StateTypeStudy]))
                configuration[ConfigName.BasePropStudy][ConfigName.StateTypeStudy] = Properties[ConfigName.StateTypeStudy];
            if(isValidValue(Properties[ConfigName.CurrentDirectionReverse]))
                configuration[ConfigName.BasePropStudy][ConfigName.CurrentDirectionReverse] = Properties[ConfigName.CurrentDirectionReverse];
        }
        this.inverseMapNewConfiguration(parent, backendPayload, configuration, generalConfig, sectionconfig)
        this.inverseAxisProperties(AxisProperties, GeneralUnit, configuration, generalConfig)
        return {configuration, generalConfig, mainName}
    },
};

const continuous_Study = 'Continuous';
const discrete_Study = 'Discrete';

const re_title_studies = /\(((PC)|(AC)|(GC)|(VD)|(AD)|(PD)|(ID)|(GD))[0-9]+\)/;
const re_name_studies = /((PC)|(AC)|(GC)|(VD)|(AD)|(PD)|(ID)|(GD))([0-9]+)?/;

const StudyManager = {
    re_title_studies,
    getStudyBase(study) {
        const matchStudy = study.match(re_name_studies);
        return matchStudy === null
            ? undefined
            : study.match(re_name_studies)[1];
    },
    getNextConfigId(study) {
        const usedStudies = Object.keys(
            store.getters.getCurrentReportViewer.configuration,
        );
        if (!usedStudies.includes(study)) return study;
        let counter = 1;
        while (usedStudies.includes(`${study}${counter}`)) counter++;
        return `${study}${counter}`;
    },
};

const PlotType = {
    PC: {
        shortText: 'Potentiometry (PC)',
        text: 'Potentiometry Continuous (PC)',
        type: continuous_Study,
        xAxisType: TypeAxis.time.id,
        yAxisType: TypeAxis.potential.id,
    },
    AC: {
        shortText: 'Amperometry (AC)',
        text: 'Amperometry Continuous (AC)',
        type: continuous_Study,
        xAxisType: TypeAxis.time.id,
        yAxisType: TypeAxis.current.id,
    },
    GC: {
        shortText: 'Generic (GC)',
        text: 'Generic Continuous (GC)',
        type: continuous_Study,
        xAxisType: '',
        yAxisType: '',
    },
    VD: {
        shortText: 'Voltammetry (VD)',
        text: 'Voltammetry Discrete (VD)',
        type: discrete_Study,
        xAxisType: TypeAxis.potential.id,
        yAxisType: TypeAxis.current.id,
    },
    AD: {
        shortText: 'Amperometry (AD)',
        text: 'Amperometry Discrete (AD)',
        type: discrete_Study,
        xAxisType: TypeAxis.time.id,
        yAxisType: TypeAxis.current.id,
    },
    PD: {
        shortText: 'Potentiometry (PD)',
        text: 'Potentiometry Discrete (PD)',
        type: discrete_Study,
        xAxisType: TypeAxis.time.id,
        yAxisType: TypeAxis.potential.id,
    },
    ID: {
        shortText: 'Impedance (ID)',
        text: 'Impedance Discrete (ID)',
        type: discrete_Study,
        xAxisType: TypeAxis.frequency.id,
        yAxisType: TypeAxis.impedance.id,
    },
    GD: {
        shortText: 'Generic (GD)',
        text: 'Generic Discrete (GD)',
        type: discrete_Study,
        xAxisType: '',
        yAxisType: '',
    },
};

export {
    PlotType,
    ConfigName,
    TypeAxis,
    PrefixUnits,
    continuous_Study,
    discrete_Study,
    NameFilesConfig,
    newConfiguration,
    StudyManager,
    SpecialDeleteName,
};
